/**
 * ============================================================================
 * useQuickCreateShortcuts — Atalhos globais de criação rápida (botão "+")
 * ----------------------------------------------------------------------------
 * Registra UM único listener global de teclado que dispara as ações
 * principais do Quick Create (Iniciar conversa, Contato, Negócio, Cliente,
 * Evento, Tarefa) a partir de qualquer tela do tenant.
 *
 * Regras de disparo:
 *  - `Alt + tecla` (ex.: Alt+N): funciona SEMPRE, inclusive com o cursor
 *    dentro de um campo de texto. É o caminho garantido.
 *  - Tecla simples (N, C, L, T, E, I): funciona apenas quando o foco NÃO
 *    está em campo editável e não há modal/diálogo/popover aberto.
 *  - Nunca dispara com Ctrl/Cmd (evita conflito com ⌘K da busca global e
 *    com atalhos nativos do navegador).
 *
 * Segurança/permissões: a lista já vem filtrada por `useAvailableQuickCreates`
 * (flags de módulo do tenant + permissões de menu do usuário). Nenhuma regra
 * de permissão é duplicada aqui.
 *
 * RELEASE: 2026-08-06 v1.1.0 - Ações marcadas com `inline` abrem o modal
 *          sobre a tela atual (QuickCreateModalsContext), sem navegar.
 * RELEASE: 2026-08-06 v1.0.0 - Criação (extrai atalhos do QuickCreateMenu e
 *          os torna globais em todas as páginas do tenant).
 * ============================================================================
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableQuickCreates } from '@/hooks/useAvailableQuickCreates';
import type { QuickCreateDef } from '@/config/quickCreate';
import { logger } from '@/lib/logger';
import {
  useQuickCreateModals,
  isQuickCreateModalKey,
} from '@/contexts/QuickCreateModalsContext';

/** Verifica se o elemento focado aceita digitação (input, textarea, editor). */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  // Editores rich text (ex.: ProseMirror/TipTap) usam contenteditable no pai.
  return !!el.closest?.('[contenteditable="true"], .ProseMirror, [role="textbox"]');
}

/** Detecta modal/diálogo aberto para não sequestrar teclas simples. */
function hasOpenOverlay(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [cmdk-root]',
  );
}

/**
 * Monta o listener global de atalhos do Quick Create.
 * Deve ser chamado UMA vez por árvore de layout (via HeaderSearchSlot).
 */
export function useQuickCreateShortcuts() {
  const navigate = useNavigate();
  const available = useAvailableQuickCreates();
  // Modais "no lugar" (null quando o provider não está montado).
  const quickModals = useQuickCreateModals();
  const modalsRef = useRef(quickModals);
  modalsRef.current = quickModals;

  // Ref evita re-registrar o listener a cada render do hook de permissões.
  const actionsRef = useRef<QuickCreateDef[]>([]);
  actionsRef.current = available.filter((a) => a.primary && a.shortcutKey);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl/Cmd reservados para atalhos do navegador e da busca global.
      if (e.metaKey || e.ctrlKey) return;

      const key = e.key?.toUpperCase();
      if (!key || key.length !== 1) return;

      const match = actionsRef.current.find((a) => a.shortcutKey === key);
      if (!match) return;

      // Tecla simples só vale fora de campos editáveis e sem overlay aberto.
      if (!e.altKey) {
        if (isEditableTarget(e.target)) return;
        if (hasOpenOverlay()) return;
      }

      e.preventDefault();
      const separator = match.route.includes('?') ? '&' : '?';
      logger.debug('QuickCreateShortcuts', 'atalho acionado', {
        key,
        alt: e.altKey,
        action: match.key,
      });

      // Ações inline não navegam: abrem o modal sobre a tela atual.
      if (match.inline && modalsRef.current && isQuickCreateModalKey(match.key)) {
        modalsRef.current.openQuickCreate(match.key);
        return;
      }

      navigate(`${match.route}${separator}new=${match.newParam}`);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
}
